/* eslint-disable react-hooks/rules-of-hooks */
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import millify from "millify";
import GradientLine from "../../assets/svgs/common/gradient-line.svg";
import type { ICampaignMetricMinimal } from "../../types/models/campaign-metric.types";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../ui/card";
import { type ChartConfig, ChartContainer, ChartTooltip } from "../ui/chart";

import { DateTime } from "luxon";
import { useMemo } from "react";
import type { DotProps } from "recharts";
import VerticalLine from "../../assets/svgs/common/vertical-line.svg";
import CustomCursor from "./chart-cursor";

const CustomActiveDotWithTooltip = (props: DotProps) => {
	const { cx, cy } = props;

	// Ensure valid coordinates for the dot
	if (!cx || !cy) return null;

	return (
		<g>
			<circle
				cursor={"pointer"}
				cx={cx}
				cy={cy}
				r={11}
				fill="rgba(255, 255, 255, 0.5)"
			/>
			<circle cursor={"pointer"} cx={cx} cy={cy} r={6} fill="white" />
		</g>
	);
};

const chartConfig = {
	date: {
		label: "Date",
	},
	value: {
		label: "Posts",
		color: "hsl(var(--slider-range))",
	},
} satisfies ChartConfig;

const transformData = (chartData: Array<{ [date: string]: number }>) => {
	return chartData.map((dataPoint) => {
		const date = Object.keys(dataPoint)[0];
		const value = dataPoint[date];
		return { date, value };
	});
};

// Function to calculate the maximum value and step size
const calculateMaxValueAndStep = (
	data: Array<{ date: string; value: number }>,
) => {
	let maxValue = Math.max(...data.map((d) => d.value));
	let step: number;
	if (maxValue <= 5) {
		step = 1;
		maxValue = 5;
	} else if (maxValue <= 10) {
		step = 1;
		maxValue = 10;
	} else if (maxValue <= 50) {
		step = 5;
		maxValue = 50;
	} else if (maxValue <= 100) {
		step = 10;
		maxValue = 100;
	} else if (maxValue <= 500) {
		step = 50;
		maxValue = 500;
	} else if (maxValue <= 1000) {
		step = 100;
		maxValue = 1000;
	} else {
		step = Number(maxValue.toPrecision(1)) / 5;
		maxValue;
	}
	const roundedMaxValue = Math.ceil(maxValue / step) * step * 2;
	return { maxValue: roundedMaxValue, step };
};

// Function to generate ticks
const generateTicks = (min: number, max: number, step: number) => {
	const ticks = [];
	for (let i = min; i <= max; i += step) {
		ticks.push(i);
	}
	return ticks;
};

export function Linechart({
	lineData,
	title,
	chartData,
	campaignMetrics,
}: {
	lineData:
		| ICampaignMetricMinimal["postsPerDay"]
		| ICampaignMetricMinimal["totalViewsChart"];
	campaignMetrics: ICampaignMetricMinimal;
	fetchingCampaignMetrics: boolean;
	chartData: Array<{ category: string; value: number | string }>;
	title: string;
}) {
	const transformedData = useMemo(
		() => transformData(lineData ?? []),
		[lineData],
	);

	const { maxValue, step } = useMemo(
		() => calculateMaxValueAndStep(transformedData),
		[transformedData],
	);

	return (
		<Card className="relative bg-transparent border-border text-primary overflow-hidden">
			<div className="top-12 -left-[26px] absolute rotate-90">
				<GradientLine />
			</div>
			<CardHeader className="flex xl:flex-row gap-4 space-y-0 items-start overflow-hidden">
				<div className="flex flex-col flex-1 justify-center gap-1">
					<CardTitle>{title}</CardTitle>
					<CardDescription className="font-bold text-secondary">
						Last updated{" "}
						{DateTime.fromJSDate(
							new Date(campaignMetrics.lastUpdatedAt ?? new Date()),
						).toRelative()}
					</CardDescription>
				</div>
				<div className="bg-statinfo border border-border rounded-[8px] w-full xl:w-auto max-w-full overflow-x-auto no-scrollbar">
					<div className="flex flex-nowrap items-center px-4 xl:px-6 py-3 xl:py-4 w-max">
						{chartData.map((data, index) => {
							return (
								// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
								<div className="flex items-center shrink-0" key={index}>
									<div className="flex flex-col items-center xl:items-start">
										<span className="flex items-center gap-2 font-semibold text-secondary text-sm xl:text-base whitespace-nowrap">
											{data.category}
										</span>
										<span className="font-bold text-lg xl:text-3xl leading-none">
											{data.value}
										</span>
									</div>
									{index < chartData.length - 1 && (
										<div className="mx-3 xl:mx-8 shrink-0 border-r border-border h-8 xl:h-auto xl:border-0">
											<span className="hidden xl:block">
												<VerticalLine />
											</span>
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>
			</CardHeader>
			<CardContent className="px-2 sm:p-6">
				<div className="w-full overflow-x-auto">
					<ChartContainer
						config={chartConfig}
						className="w-full h-[250px] aspect-auto"
						style={{ minWidth: Math.max(transformedData.length * 60, 640) }}
					>
						<LineChart
							accessibilityLayer
							height={450}
							data={transformedData}
							margin={{
								top: 1,
								left: 20,
								right: 12,
							}}
						>
							<CartesianGrid
								stroke="#2f2f2f"
								vertical={false}
								strokeDasharray="3 3"
							/>
							<XAxis
								dataKey="date"
								tickLine={false}
								axisLine={false}
								tickMargin={12}
								minTickGap={32}
								tickFormatter={(value) => {
									return value.toLocaleString();
								}}
							/>
							<YAxis
								tickLine={false}
								axisLine={false}
								tickMargin={40}
								domain={[0, maxValue]}
								ticks={generateTicks(0, maxValue, step)}
								tickFormatter={(value) => millify(value)}
							/>

							<Line
								dataKey="value"
								type="monotone"
								stroke={"var(--slider-range)"}
								strokeWidth={2}
								dot={false}
								activeDot={<CustomActiveDotWithTooltip />} // Custom dot with hover
								cursor={"pointer"}
							/>
							<ChartTooltip
								cursor={
									<CustomCursor
										stroke="white"
										strokeWidth={1}
										strokeDasharray="3 3"
										maxValue={maxValue}
									/>
								}
								content={() => null}
							/>
						</LineChart>
					</ChartContainer>
				</div>
			</CardContent>
		</Card>
	);
}
